import { AccountTradingSnapshot } from 'src/hooks/query/snapshots/account/types';

interface Params {
	snapshots: AccountTradingSnapshot[];
}

export default function computeSharpeRatioFromSnapshots({ snapshots }: Params) {
	const points = toPnlPoints(snapshots);
	if (points.length < 3) {
		return 0;
	}

	const dailyPoints = groupLastPointPerDay(points);
	const useDaily = dailyPoints.length >= 3;

	const { pnlChanges, avgDeltaSeconds } = computeDollarPnlChanges(
		useDaily ? dailyPoints : points
	);

	if (pnlChanges.length < 2) {
		return 0;
	}

	const { mean, std } = meanStd(pnlChanges);
	if (std === 0) {
		return 0;
	}

	const secondsPerYear = 365 * 24 * 60 * 60;
	const periodsPerYear = (() => {
		if (useDaily) {
			return 365;
		}

		if (avgDeltaSeconds > 0) {
			return secondsPerYear / avgDeltaSeconds;
		}

		return 0;
	})();

	if (periodsPerYear <= 0) {
		return 0;
	}

	const sharpe = (mean / std) * Math.sqrt(periodsPerYear);

	return Number.isFinite(sharpe) ? sharpe : 0;
}

interface PnlPoint {
	ts: number;
	accountValue: number;
	allTimeTotalPnl: number;
}

function toPnlPoints(points: AccountTradingSnapshot[]): PnlPoint[] {
	return points
		.filter(
			(p) =>
				p.ts &&
				Number.isFinite(p.accountBalance ?? NaN) &&
				Number.isFinite((p.cumulativeRealizedPnl ?? 0) + (p.unrealizedPnl ?? 0))
		)
		.map((p) => ({
			ts: p.ts,
			accountValue: p.accountBalance ?? 0,
			allTimeTotalPnl: (p.cumulativeRealizedPnl ?? 0) + (p.unrealizedPnl ?? 0),
		}))
		.sort((a, b) => a.ts - b.ts);
}

function groupLastPointPerDay(points: PnlPoint[]): PnlPoint[] {
	if (points.length === 0) {
		return [];
	}

	const byDay = new Map<string, PnlPoint>();

	for (const p of points) {
		const dayKey = new Date(p.ts * 1000).toISOString().slice(0, 10);
		const prev = byDay.get(dayKey);

		if (!prev || p.ts > prev.ts) {
			byDay.set(dayKey, p);
		}
	}

	return Array.from(byDay.values()).sort((a, b) => a.ts - b.ts);
}

function computeDollarPnlChanges(points: PnlPoint[]): {
	pnlChanges: number[];
	avgDeltaSeconds: number;
} {
	const pnlChanges: number[] = [];
	let deltaSumSec = 0;
	let deltaCount = 0;

	for (let i = 1; i < points.length; i++) {
		const prev = points[i - 1];
		const curr = points[i];

		const deltaPnl = curr.allTimeTotalPnl - prev.allTimeTotalPnl;

		if (!Number.isFinite(deltaPnl)) {
			continue;
		}

		pnlChanges.push(deltaPnl);
		const dt = Math.max(1, curr.ts - prev.ts);
		deltaSumSec += dt;
		deltaCount += 1;
	}

	const avgDeltaSeconds = deltaCount > 0 ? deltaSumSec / deltaCount : 0;
	return { pnlChanges, avgDeltaSeconds };
}

function meanStd(values: number[]): { mean: number; std: number } {
	const n = values.length;
	if (n === 0) {
		return { mean: 0, std: 0 };
	}

	const mean = values.reduce((a, b) => a + b, 0) / n;

	let varSum = 0;

	for (let i = 0; i < n; i++) {
		const d = values[i] - mean;
		varSum += d * d;
	}

	const std = n > 1 ? Math.sqrt(varSum / (n - 1)) : 0;

	return { mean, std };
}
